import { ReactNode } from 'react';

import { SelectOption } from '@components/select';
import {
  FieldMappingFieldItem,
  FieldMappingGroup,
  FieldType,
  VariableValue,
} from '@components/text-editors/addons/field-mapping';
import { findVariableField } from '@components/text-editors/addons/field-mapping-variable-label';

export const filterSelectAttributeVariables = (
  fields: FieldMappingFieldItem[],
): FieldMappingFieldItem[] => {
  const seenBySourceAndLabel = new Set<string>();

  return fields.reduce((acc, field) => {
    if (field.type === 'select') {
      const baseLabel = field.label.replace(/\s*\((value|label)\)$/, '');
      const key = `${field.source}::${baseLabel}`;
      if (!seenBySourceAndLabel.has(key)) {
        seenBySourceAndLabel.add(key);
        acc.push({
          ...field,
          field: 'value',
          label: baseLabel,
        });
      }
    } else if (field.type === 'multi_select') {
      const baseLabel = field.label.replace(/\s*\((values|labels)\)$/, '');
      const key = `${field.source}::${baseLabel}`;
      if (!seenBySourceAndLabel.has(key)) {
        seenBySourceAndLabel.add(key);
        acc.push({
          ...field,
          field: 'values',
          label: baseLabel,
        });
      }
    } else {
      acc.push(field);
    }
    return acc;
  }, [] as FieldMappingFieldItem[]);
};

/**
 * Select attributes are exposed to the builder under two sibling discriminators
 * that point at the *same* underlying field (identical type and options): the
 * value variant (`value` / `values`) and the label variant (`label` / `labels`).
 * The backend only emits one of the pair when the options are opaque — an
 * attribute backed by a hidden, non-marketplace option list exposes only the
 * label variant — but a saved condition can reference either. When the exact
 * discriminator is no longer present we still resolve the field via its sibling
 * so the editor can determine the value type and options; otherwise the
 * comparator and value inputs fall back to a text field and render blank.
 */
const SELECT_DISCRIMINATOR_SIBLINGS: Record<string, string> = {
  value: 'label',
  label: 'value',
  values: 'labels',
  labels: 'values',
};

const SELECT_FIELD_TYPES = new Set<FieldType>(['select', 'multi_select']);

export const findMappedField = (
  formValue: VariableValue | undefined,
  variableGroups: FieldMappingGroup[],
): FieldMappingFieldItem | undefined => {
  if (!formValue || !formValue.variables) {
    return undefined;
  }
  const variable = Object.values(formValue.variables)[0];
  const fields = variableGroups.flatMap((obj) => obj.fields);
  const exactMatch = variable ? findVariableField(variable, fields) : undefined;
  if (exactMatch || variable?.source === 'action') {
    return exactMatch;
  }

  const siblingField = variable?.field ? SELECT_DISCRIMINATOR_SIBLINGS[variable.field] : undefined;
  if (siblingField) {
    return fields.find(
      (field) =>
        field.source === variable?.source &&
        field.field === siblingField &&
        SELECT_FIELD_TYPES.has(field.type as FieldType),
    );
  }

  return undefined;
};

export const findValueType = (mappedField?: FieldMappingFieldItem): string => {
  const typeMapping: Partial<Record<FieldType, string>> = {
    select: 'select',
    multi_select: 'multi_select',
    date: 'date',
    boolean: 'boolean',
    file: 'file',
    number: 'number',
    text: 'text',
    decimal: 'number',
  };

  return typeMapping[mappedField?.type ?? 'text'] ?? 'text';
};

type MappedFieldOption = NonNullable<FieldMappingFieldItem['options']>[number];

const toSelectOption = (option: MappedFieldOption): SelectOption => {
  return 'display' in option ? { value: option.value, label: option.display } : option;
};

export const toValueOption = (option: MappedFieldOption): SelectOption => {
  const { value, label, filterText } = toSelectOption(option);
  if (filterText !== undefined) {
    return { value, label, filterText };
  }
  return { value, label: typeof label === 'string' ? label : value };
};

export const findValueOptions = (mappedField?: FieldMappingFieldItem) => {
  if (!mappedField?.options) {
    return [];
  }
  return mappedField.options.map((option) => {
    const optionValue = (
      'shortId' in option && option.shortId ? option.shortId : option.value
    ) as string;
    const altValue = 'shortId' in option && option.shortId ? (option.value as string) : undefined;
    // Spreading keeps the label/filterText pairing intact for options that
    // carry a non-string label; shortId is already folded into value/altValue.
    const { shortId: _shortId, ...rest } = toSelectOption(option) as SelectOption & {
      shortId?: string | null;
    };
    const labelled: SelectOption =
      rest.label == null ? { ...rest, label: option.value as string } : rest;
    return { ...labelled, value: optionValue, altValue };
  });
};

export const isVariableValue = (raw: unknown): raw is VariableValue => {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return false;
  }
  const obj = raw as Record<string, unknown>;
  if ('label' in obj) {
    return false;
  }
  return typeof obj.value === 'string';
};

export const getVariableValueLabel = (variableValue: VariableValue): string => {
  if (variableValue.variables) {
    const firstVariable = Object.values(variableValue.variables)[0];
    if (firstVariable?.label) {
      return firstVariable.label;
    }
  }
  return variableValue.value;
};

export const toVariableValue = (raw: unknown): VariableValue => {
  if (raw == null) {
    return { value: '' };
  }
  if (typeof raw === 'string') {
    return { value: raw };
  }
  if (typeof raw === 'number' || typeof raw === 'boolean') {
    return { value: String(raw) };
  }
  if (Array.isArray(raw)) {
    return toVariableValue(raw[0]);
  }
  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    const innerValue = typeof obj.value === 'string' ? obj.value : '';
    if ('variables' in obj || 'type' in obj) {
      return obj as VariableValue;
    }
    return { value: innerValue };
  }
  return { value: '' };
};

export type ConditionValueOption = {
  value: string;
  label: ReactNode;
  altValue?: string;
};

export type ReconciledConditionValue = {
  value: unknown;
  isStale: boolean;
  staleLabels: string[];
};

export type ConditionValueSnapshot = {
  value: string;
  label: string;
};

const reconcileSingleValue = (
  value: unknown,
  options: ConditionValueOption[],
): ReconciledConditionValue => {
  if (typeof value === 'object' && value !== null && 'variables' in value) {
    return { value, isStale: false, staleLabels: [] };
  }

  const id = typeof value === 'string' ? value : (value as { value?: string })?.value;
  if (!id) {
    return { value, isStale: false, staleLabels: [] };
  }

  const matchedOption = options.find((option) => {
    return option.value === id || (option.altValue !== undefined && option.altValue === id);
  });
  if (!matchedOption) {
    const storedLabel =
      typeof value === 'object' && value !== null
        ? (value as { label?: unknown }).label
        : undefined;
    const staleLabel =
      typeof storedLabel === 'string'
        ? storedLabel
        : typeof value === 'string'
          ? value
          : String(id);
    return { value, isStale: true, staleLabels: [staleLabel] };
  }

  const storedLabel =
    typeof value === 'object' && value !== null ? (value as { label?: unknown }).label : undefined;
  const label =
    typeof matchedOption.label === 'string'
      ? matchedOption.label
      : typeof storedLabel === 'string'
        ? storedLabel
        : id;

  const snapshot: ConditionValueSnapshot = { value: id, label };
  return { value: snapshot, isStale: false, staleLabels: [] };
};

export const reconcileValueWithOptions = (
  value: unknown,
  options: ConditionValueOption[],
): ReconciledConditionValue => {
  if (options.length === 0) {
    return { value, isStale: false, staleLabels: [] };
  }
  if (value === undefined || value === null || value === '') {
    return { value, isStale: false, staleLabels: [] };
  }

  if (Array.isArray(value)) {
    const reconciledEntries = value.map((entry) => {
      return reconcileSingleValue(entry, options);
    });
    return {
      value: reconciledEntries.map((entry) => {
        return entry.value;
      }),
      isStale: reconciledEntries.some((entry) => {
        return entry.isStale;
      }),
      staleLabels: Array.from(
        new Set(
          reconciledEntries.flatMap((entry) => {
            return entry.staleLabels;
          }),
        ),
      ),
    };
  }

  return reconcileSingleValue(value, options);
};
